// The motif of a blunder or a miss: one classification shared by the
// Blunders and Misses screens, the library's `mistake` filter and the
// puzzles' metadata (2026-10-12). Named like an opening: a family and a
// variation ("Pin · against the queen"), after Lichess's puzzle themes, the
// one public machine-tagged taxonomy. This file is the contract only: the
// vocabulary, its names, the service's id and its input. The identification
// itself is plugin `shared-mistake-motifs`, reached through `MOTIFS_API`.
//
// What a motif means: for a blunder, the tactic the opponent gets after the
// played move (read on the position after it, from the opponent's best
// reply); for a miss, the tactic the reader could have played (read on the
// position before, from the reader's best line); a puzzle's motif is that of
// the mistake it was made from. `positional` when no tactic explains it.
import type { Color, MistakeFact } from '@chess/api-model';

export type MotifFamily =
  | 'pin'
  | 'skewer'
  | 'fork'
  | 'discoveredAttack'
  | 'hangingPiece'
  | 'trappedPiece'
  | 'captureDefender'
  | 'mate'
  | 'pawn'
  | 'kingAttack'
  | 'combination'
  | 'positional';

export const MOTIF_FAMILIES = [
  'pin',
  'skewer',
  'fork',
  'discoveredAttack',
  'hangingPiece',
  'trappedPiece',
  'captureDefender',
  'mate',
  'pawn',
  'kingAttack',
  'combination',
  'positional',
] as const satisfies readonly MotifFamily[];

/** A family and, when the family has them, one of its variations (`MOTIF_VARIATIONS`). */
export interface Motif {
  family: MotifFamily;
  variation?: string;
}

/**
 * Each family's variations, as the keys spell them. What the variation
 * names: pin, the piece behind (king is the absolute pin); skewer, the piece
 * in front; fork, the forking piece; discovered attack, what is uncovered;
 * hanging and trapped piece, the piece; capture the defender, what it
 * defended; mate, the pattern; pawn and king attack, the theme; combination,
 * the device. Positional has none.
 */
export const MOTIF_VARIATIONS: Readonly<Record<MotifFamily, readonly string[]>> = Object.freeze({
  pin: ['king', 'queen', 'rook'],
  skewer: ['king', 'queen', 'rook'],
  fork: ['knight', 'pawn', 'bishop', 'rook', 'queen', 'king'],
  discoveredAttack: ['check', 'doubleCheck', 'queen', 'rook'],
  hangingPiece: ['queen', 'rook', 'minor', 'pawn'],
  trappedPiece: ['queen', 'rook', 'minor'],
  captureDefender: ['mate', 'piece'],
  mate: ['backRank', 'smothered', 'anastasia', 'boden', 'corner', 'arabian', 'hook', 'dovetail', 'epaulette', 'doubleBishop', 'other'],
  pawn: ['promotion', 'underpromotion', 'passedPawn', 'advancedPawn'],
  kingAttack: ['f2f7', 'exposedKing', 'kingside', 'queenside'],
  combination: ['deflection', 'attraction', 'interference', 'clearance', 'intermezzo', 'xRay', 'sacrifice', 'zugzwang'],
  positional: [],
});

export const FAMILY_LABEL: Readonly<Record<MotifFamily, string>> = Object.freeze({
  pin: 'Pin',
  skewer: 'Skewer',
  fork: 'Fork',
  discoveredAttack: 'Discovered attack',
  hangingPiece: 'Hanging piece',
  trappedPiece: 'Trapped piece',
  captureDefender: 'Capture the defender',
  mate: 'Mate',
  pawn: 'Pawn',
  kingAttack: 'King attack',
  combination: 'Combination',
  positional: 'Positional',
});

/** Each variation in words, read after its family: "Pin · against the queen", "Fork · by a knight". */
export const VARIATION_LABEL: Readonly<Record<MotifFamily, Readonly<Record<string, string>>>> = Object.freeze({
  pin: { king: 'against the king', queen: 'against the queen', rook: 'against a rook' },
  skewer: { king: 'of the king', queen: 'of the queen', rook: 'of a rook' },
  fork: { knight: 'by a knight', pawn: 'by a pawn', bishop: 'by a bishop', rook: 'by a rook', queen: 'by the queen', king: 'by the king' },
  discoveredAttack: { check: 'discovered check', doubleCheck: 'double check', queen: 'on the queen', rook: 'on a rook' },
  hangingPiece: { queen: 'the queen', rook: 'a rook', minor: 'a minor piece', pawn: 'a pawn' },
  trappedPiece: { queen: 'the queen', rook: 'a rook', minor: 'a minor piece' },
  captureDefender: { mate: 'of the mating square', piece: 'of a piece' },
  mate: {
    backRank: 'back rank',
    smothered: 'smothered',
    anastasia: "Anastasia's",
    boden: "Boden's",
    corner: 'corner',
    arabian: 'Arabian',
    hook: 'hook',
    dovetail: 'dovetail',
    epaulette: 'epaulette',
    doubleBishop: 'double bishop',
    other: 'other',
  },
  pawn: { promotion: 'promotion', underpromotion: 'underpromotion', passedPawn: 'passed pawn', advancedPawn: 'advanced pawn' },
  kingAttack: { f2f7: 'on f2/f7', exposedKing: 'exposed king', kingside: 'on the kingside', queenside: 'on the queenside' },
  combination: {
    deflection: 'deflection',
    attraction: 'attraction',
    interference: 'interference',
    clearance: 'clearance',
    intermezzo: 'intermezzo',
    xRay: 'x-ray',
    sacrifice: 'sacrifice',
    zugzwang: 'zugzwang',
  },
  positional: {},
});

export function isMotifFamily(value: string): value is MotifFamily {
  return (MOTIF_FAMILIES as readonly string[]).includes(value);
}

/** Whether a family has this variation. */
export function isMotifVariation(family: MotifFamily, variation: string): boolean {
  return MOTIF_VARIATIONS[family].includes(variation);
}

export function familyLabel(family: MotifFamily): string {
  return FAMILY_LABEL[family];
}

/** The variation in words; undefined for one the family does not have. */
export function variationLabel(family: MotifFamily, variation: string): string | undefined {
  return VARIATION_LABEL[family][variation];
}

/** The motif in words: "Pin · against the queen"; the family alone without a variation ("Positional"). */
export function motifLabel(motif: Motif): string {
  const family = familyLabel(motif.family);
  const variation = motif.variation ? variationLabel(motif.family, motif.variation) : undefined;
  return variation ? `${family} · ${variation}` : family;
}

/** A motif as one word for keys and queries: `pin`, `pin/queen`. */
export function motifId(motif: Motif): string {
  return motif.variation ? `${motif.family}/${motif.variation}` : motif.family;
}

/** The motif an id names; undefined for a family or a variation the scheme lacks. */
export function parseMotifId(id: string): Motif | undefined {
  const [family, variation, ...rest] = id.split('/');
  if (rest.length || !isMotifFamily(family)) return undefined;
  if (variation === undefined) return { family };
  return isMotifVariation(family, variation) ? { family, variation } : undefined;
}

/** Every motif id the scheme has: each family, then each of its variations, in order. */
export const MOTIF_IDS: readonly string[] = MOTIF_FAMILIES.flatMap((family) => [family, ...MOTIF_VARIATIONS[family].map((v) => `${family}/${v}`)]);

// ---- the service -------------------------------------------------------------

/** The registry name of the motif classifier (plugin `shared-mistake-motifs`). */
export const MOTIFS_API = 'mistakes.motifs';
/** 1.1.0 (2026-10-12): `referencesFor`. */
export const MOTIFS_API_VERSION = '1.1.0';

/** Where a learning reference lives. */
export type ReferenceSource = 'lichess' | 'wikipedia' | 'chesscom';
export const REFERENCE_SOURCES = ['lichess', 'wikipedia', 'chesscom'] as const satisfies readonly ReferenceSource[];
/** What it is: an interactive lesson or puzzle set, a study to play through, or an article to read. */
export type ReferenceKind = 'lesson' | 'study' | 'article';
export const REFERENCE_KINDS = ['lesson', 'study', 'article'] as const satisfies readonly ReferenceKind[];

/**
 * One page to learn a motif from, verified by hand (plugin
 * `shared-mistake-motifs`, `references.json`): what the puzzles' Learn card
 * links to, outside the app.
 */
export interface Reference {
  title: string;
  /** https only. */
  url: string;
  source: ReferenceSource;
  kind: ReferenceKind;
  /** One line on what the page covers. */
  summary: string;
}

/**
 * What the classifier reads: the fields of a `MistakeFact` that hold the
 * positions and the engine's lines, plus the reader's colour. A fact spread
 * with `reader` is one.
 */
export interface MotifInput extends Pick<MistakeFact, 'kind' | 'fenBefore' | 'fenAfter' | 'best' | 'reply' | 'lost' | 'missed'> {
  /** Whose mistake it is: the side that played the move at `fenBefore`. */
  reader: Color;
}

export interface Motifs {
  /** The motif of one mistake; never throws, `positional` when nothing is found. */
  classify(input: MotifInput): Motif;
  /**
   * Up to three pages to learn the motif from (2026-10-12): the variation's
   * own, then the family's when the variation has fewer, lichess (lessons
   * first), then Wikipedia, then chess.com. Empty for a motif without any.
   */
  referencesFor(motif: Motif): Reference[];
}

/** The motif of one mistake fact of the reader's: what the keys and the screens read, the service behind it. */
export type MotifOf = (m: MistakeFact, reader: Color) => Motif;

/**
 * The service as a `MotifOf` that classifies each fact once: the answer is
 * held by the fact object (a `WeakMap`), so the store's every `resolveFilter`
 * and the screens' every aggregation read the same motif without asking the
 * service again, and a game read anew (new fact objects) is classified anew.
 */
export function memoisedMotifs(motifs: Pick<Motifs, 'classify'>): MotifOf {
  const held = new WeakMap<MistakeFact, Motif>();
  return (m, reader) => {
    const known = held.get(m);
    if (known) return known;
    const motif = motifs.classify({ ...m, reader });
    held.set(m, motif);
    return motif;
  };
}
